// src/pages/league-dashboard/TeamHomeDashboard.js
import React, { useState, useEffect } from 'react';
import { dynastyTheme } from '../../services/colorService';
import { leaguesAPI } from '../../services/apiService';
import { getTotalSalaryCap } from '../../services/contractUtils';
import { User, Star, Search, DollarSign, Clock } from 'lucide-react';
import TeamPageHeader from '../../components/common/TeamPageHeader';
import PlayerSearchDropdownLeague from '../../components/PlayerSearchDropdownLeague';
import PositionAssignmentDropdown from '../../components/league-dashboard/PositionAssignmentDropdown';
import DropDesignationModal from '../../components/modals/DropDesignationModal';
import { getWaiverActionState } from '../../utils/waiverStatus';
import { useWaiverClaimFlow } from '../../hooks/useWaiverClaimFlow';
import { useNavigate } from 'react-router-dom';
import { fetchBoxScore, fetchAvailableDates, fetchBoxScoreRankings } from '../../services/boxScoreApi';
import PlayerLink from '../../components/PlayerLink';
import PlayerNotes from './team-home/PlayerNotes';
import WatchListWidget from './team-home/WatchListWidget';
import StartingPitchers from './team-home/StartingPitchers';
import LastNightBox from './team-home/LastNightBox';

const TeamHomeDashboard = ({ leagueId, teamId, currentUser, league, userTeam, bannerConfig }) => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [teamInfo, setTeamInfo] = useState(null);
  const [boxScoreData, setBoxScoreData] = useState(null);
  const [boxScoreDate, setBoxScoreDate] = useState(null);
  const [boxScoreRankings, setBoxScoreRankings] = useState(null);
  const navigate = useNavigate();
  
  // Position modal state
  const [showPositionModal, setShowPositionModal] = useState(false);
  const [playerForAssignment, setPlayerForAssignment] = useState(null);
  const [currentRoster, setCurrentRoster] = useState([]);

  // DD-WAIVER-0723-BUILD W2: waiver-claim flow for the team-home ADD path.
  // On-waivers players route into the claim modal BEFORE position
  // assignment (same submit flow as FA-search).
  const waiverClaim = useWaiverClaimFlow({
    leagueId,
    league,
    currentRoster,
    onSuccess: (claimedPlayer, message) => { alert(message); },
    onError: (message) => { alert(message); },
  });

  useEffect(() => {
    fetchTeamDashboard();
    fetchTeamInfo();
    loadRoster(); // Load roster for position assignments
    fetchYesterdayBoxScore(); // Fetch box score for yesterday
  }, [leagueId, teamId]);

  const fetchTeamInfo = async () => {
    try {
      const response = await leaguesAPI.getUserTeam(leagueId);
      if (response.success) {
        setTeamInfo(response);
      }
    } catch (error) {
      console.log('Could not fetch team info:', error);
    }
  };

  const fetchYesterdayBoxScore = async () => {
    try {
      // First, get available dates
      // Don't pass season_year — let the backend use the league's current_season
      const datesResponse = await fetchAvailableDates(leagueId, teamId);
      
      if (datesResponse.success && datesResponse.dates && datesResponse.dates.length > 0) {
        // Use the most recent date (dates are sorted newest first)
        const mostRecentDate = datesResponse.dates[0];
        console.log(`Fetching most recent box score: ${mostRecentDate}`);
        setBoxScoreDate(mostRecentDate);
        
        const response = await fetchBoxScore(leagueId, teamId, mostRecentDate);
        
        if (response.success && response.box_score) {
          setBoxScoreData({
            hitting: response.box_score.hitting ?? [],
            pitching: response.box_score.pitching ?? [],
            team_totals: response.box_score.team_totals ?? {}
          });
        } else {
          // No box score available
          setBoxScoreData({ hitting: [], pitching: [] });
        }
        
        // Fetch rankings - wrapped in try-catch to catch specific errors
        try {
          console.log('🏆 About to call fetchBoxScoreRankings...');
          const rankingsResponse = await fetchBoxScoreRankings(leagueId, teamId, mostRecentDate);
          console.log('🔍 Rankings API Response:', rankingsResponse);
          if (rankingsResponse.success) {
            // Store the full rankings response (includes your_ranks, all_rankings, stats_date)
            console.log('🔍 Setting rankings:', rankingsResponse);
            setBoxScoreRankings({
              your_ranks: rankingsResponse.your_ranks ?? {},
              all_rankings: rankingsResponse.all_rankings ?? {},
              // String `||` (not `??`): empty stats_date string treated as missing; fall to mostRecentDate fallback.
              stats_date: rankingsResponse.stats_date || mostRecentDate
            });
          } else {
            console.log('⚠️ Rankings not found in response:', rankingsResponse);
            setBoxScoreRankings(null);
          }
        } catch (rankingsError) {
          console.error('❌ ERROR fetching rankings:', rankingsError);
          console.error('❌ Error details:', rankingsError.message, rankingsError.stack);
          setBoxScoreRankings(null);
        }
      } else {
        // No dates available
        console.log('No box score dates available');
        setBoxScoreData({ hitting: [], pitching: [] });
      }
    } catch (err) {
      console.error('Failed to load box score:', err);
      setBoxScoreData({ hitting: [], pitching: [] });
    }
  };

  const fetchTeamDashboard = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Try to get team data - if it fails, create empty structure
      try {
        const data = await leaguesAPI.getTeamHomeData(leagueId);
        
        // Even if no players on roster, show the dashboard structure
        // Ensure roster_stats is always an array
        const rosterStats = Array.isArray(data.roster_stats) ? data.roster_stats : [];
        
        setDashboardData({
          // String `||` (not `??`) in nested team_name template: empty firstName treated as missing → 'Home'.
          team_info: data.team_info ?? {
            team_name: `Team ${currentUser?.firstName || 'Home'}`,
            team_id: teamId
          },
          roster_stats: rosterStats,
          starting_pitchers: Array.isArray(data.starting_pitchers) ? data.starting_pitchers : [],
          player_notes: Array.isArray(data.player_notes) ? data.player_notes : [],
          last_night_box: data.last_night_box ?? { hitting: [], pitching: [] }
        });
      } catch (apiError) {
        console.log('API error, using default structure:', apiError);
        // Even if API fails, show empty dashboard
        setDashboardData({
          team_info: {
            // String `||` (not `??`): empty firstName string treated as missing → 'Home'.
            team_name: `Team ${currentUser?.firstName || 'Home'}`,
            team_id: teamId
          },
          roster_stats: [],
          starting_pitching: [],
          player_notes: [],
          last_night_box: { hitting: [], pitching: [] }
        });
      }
    } catch (err) {
      console.error('Dashboard fetch error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };


  // Load current roster for position analysis
  const loadRoster = async () => {
    try {
      const response = await leaguesAPI.getMyRosterCanonical(leagueId);
      if (response.success) {
        setCurrentRoster(response.players ?? []);
      }
    } catch (err) {
      console.error('Error loading roster:', err);
    }
  };

  // Handler for opening position modal
  const handleOpenPositionModal = (player) => {
    // W2 waiver gate (Bible § 4.5.19): an on-waivers player with an active
    // hold gets the CLAIM flow, never position assignment / plain ADD.
    // Post-expiry falls through to the existing ADD behavior.
    const waiverState = getWaiverActionState(player, teamId || null);
    if (waiverState.onWaivers && !waiverState.expired) {
      if (waiverState.coolingOff) {
        alert('Cooling Off — you cut this player; wait for waivers to clear.');
        return;
      }
      waiverClaim.openClaim(player);
      return;
    }

    console.log('🔍 Opening position modal for:', player);
    setPlayerForAssignment(player);
    setShowPositionModal(true);
  };

  // Handler for position assignment
  const handlePositionAssignment = async (assignmentData) => {
    try {
      const player = assignmentData.player;
      const assignment = assignmentData.assignment;
      
      // Extract the correct data from the player object
      const leaguePlayerId = player.ids?.league_player;
      // String `||` (not `??`) on 2 lines: empty first/last name strings treated as missing.
      const firstName = player.info?.first_name || '';
      const lastName = player.info?.last_name || '';
      
      // CRITICAL: Use the salary/contract from the player object
      let playerSalary = player.league_context?.financial?.contract_salary;
      let contractYears = player.league_context?.financial?.contract_years;
      
      // For minors, salary is 0
      if (assignmentData.roster_status === 'minors') {
        playerSalary = 0;
        contractYears = 0;
      }

      const playerData = {
        league_player_id: leaguePlayerId,
        salary: playerSalary,
        contract_years: contractYears,
        roster_status: assignmentData.roster_status,
        roster_position: assignmentData.roster_position,
        start_contract: assignmentData.start_contract
      };
      
      console.log('🔍 Adding player with data:', {
        player_name: `${firstName} ${lastName}`,
        ...playerData,
        original_salary: player.league_context?.financial?.contract_salary,
        original_contract: player.league_context?.financial?.contract_years
      });

      const response = await leaguesAPI.addPlayerToTeam(leagueId, playerData);

      if (response.success) {
        console.log('✅ Player added successfully:', response);
        await fetchTeamDashboard(); // Reload dashboard
        await loadRoster(); // Reload roster for next addition
        setShowPositionModal(false);
        setPlayerForAssignment(null);
      } else {
        console.error('❌ Failed to add player:', response);
        // String `||` (not `??`): empty error-message chain — fall through to response.error.
        alert(`Failed to add player: ${response.message || response.error}`);
      }
    } catch (err) {
      console.error('❌ Error adding player:', err);
      alert(`Error adding player: ${err.message}`);
    }
  };

  // Handler for player transactions (legacy)
  const handlePlayerAdded = (player) => {
    console.log('Player added to team:', player);
    fetchTeamDashboard(); // Reload dashboard data
    loadRoster(); // Reload roster
  };

  const handlePlayerDropped = (player) => {
    console.log('Player dropped from team:', player);
    fetchTeamDashboard(); // Reload dashboard data
    loadRoster(); // Reload roster
  };

  if (loading) {
    return (
      <div className={dynastyTheme.components.card.base}>
        <div className={`${dynastyTheme.classes.text.white} p-8 text-center`}>
          <div className={`animate-spin rounded-full h-8 w-8 border-b-2 ${dynastyTheme.classes.border.primary} mx-auto mb-4`}></div>
          Loading team dashboard...
        </div>
      </div>
    );
  }

  const { 
    team_info, 
    roster_stats, 
    starting_pitchers, 
    player_notes, 
    last_night_box 
  } = dashboardData ?? {};

  // Ensure all array fields are actually arrays
  const safeRosterStats = Array.isArray(roster_stats) ? roster_stats : [];
  const safeStartingPitchers = Array.isArray(starting_pitchers) ? starting_pitchers : [];
  const safePlayerNotes = Array.isArray(player_notes) ? player_notes : [];

  // String `||` (not `??`) chain: empty team_name strings treated as missing through fallback chain.
  const displayTeamName = teamInfo?.team_name || team_info?.team_name || 'My Team';
  const logoUrl = teamInfo?.team_logo_url;
  const teamColors = teamInfo?.team_colors;
  const teamMotto = teamInfo?.team_motto;

  return (
    <div className="space-y-6">
      <TeamPageHeader
        title={displayTeamName}
        logoUrl={logoUrl}
        teamName={displayTeamName}
        teamColors={teamColors}
        teamMotto={teamMotto}
        bannerConfig={bannerConfig}
        badges={false}
        subtitle={
          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-2">
              <User className={`w-4 h-4 ${dynastyTheme.classes.text.primary}`} />
              <span className={dynastyTheme.classes.text.neutralLight}>
                Manager: <span className={dynastyTheme.classes.text.white}>
                  {/* String `||` chain: empty owner-name strings treated as missing through fallback chain. */}
                  {teamInfo?.team?.owner_name || teamInfo?.owner_name || currentUser?.firstName || 'Unknown'}
                </span>
              </span>
            </div>
            {safeRosterStats.length > 0 && (
              <div className="flex items-center gap-2">
                <Star className={`w-4 h-4 ${dynastyTheme.classes.text.primary}`} />
                <span className={dynastyTheme.classes.text.neutralLight}>
                  Roster: <span className={dynastyTheme.classes.text.white}>{safeRosterStats.length} players</span>
                </span>
              </div>
            )}
          </div>
        }
        actions={
          <div className="flex flex-col gap-3 items-end">
            <button
              onClick={() => { window.location.hash = `/leagues/${leagueId}/roster`; }}
              className={dynastyTheme.utils.getComponent('button', 'secondary', 'md')}
            >
              Edit Team
            </button>
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-green-400" />
                <span className={dynastyTheme.classes.text.neutralLight}>Total Salary:</span>
                <span className="text-green-400 font-semibold">
                  ${safeRosterStats.reduce((sum, p) => sum + (p.league_context?.financial?.contract_salary ?? 0), 0)}M / ${getTotalSalaryCap(league?.salary_settings)}M
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-yellow-400" />
                <span className={dynastyTheme.classes.text.neutralLight}>Expiring:</span>
                <span className="text-yellow-400 font-semibold">
                  {safeRosterStats.filter(p => p.league_context?.financial?.contract_years === 1 || p.league_context?.financial?.contract_type === 'OP').length} contracts
                </span>
              </div>
            </div>
          </div>
        }
      />

      {/* Top Row: Last Night's Box (Full Width) */}
      <LastNightBox 
        boxData={boxScoreData} 
        boxDate={boxScoreDate} 
        rankings={boxScoreRankings}
        leagueId={leagueId}
        teamId={teamId}
      />

      {/* Middle Row: Starting Pitchers + Player Notes */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <StartingPitchers starters={safeStartingPitchers} rosterPlayers={safeRosterStats} />
        <PlayerNotes notes={safePlayerNotes} rosterPlayers={safeRosterStats} />
      </div>

      {/* Bottom Row: Watch List Widget */}
      <WatchListWidget league={league} />

      {/* W2: waiver-claim modal (context='waiver_claim') — same submit flow
          as FA-search (leaguesAPI.submitWaiverClaim via useWaiverClaimFlow). */}
      <DropDesignationModal {...waiverClaim.modalProps} />
    </div>
  );
};


export default TeamHomeDashboard;